import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, PlayCircle, RefreshCw } from "lucide-react";
import { auditBousalaMajors, preflightBousala, publishBousala, reseedBousalaMajors, runQaFixtures } from "@/lib/bousala/admin.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const auditQ = () => queryOptions({ queryKey: ["admin", "bousala", "majors-audit"], queryFn: () => auditBousalaMajors() });
const preflightQ = () => queryOptions({ queryKey: ["admin", "bousala", "preflight"], queryFn: () => preflightBousala() });

export const Route = createFileRoute("/_authenticated/admin/bousala/settings")({
  loader: ({ context }) => Promise.all([
    context.queryClient.ensureQueryData(auditQ()),
    context.queryClient.ensureQueryData(preflightQ()),
  ]),
  head: () => ({ meta: [{ title: "إعدادات بوصلة د. سارة" }, { name: "robots", content: "noindex" }] }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: BousalaSettingsPage,
});

function BousalaSettingsPage() {
  const audit = useQuery(auditQ());
  const preflight = useQuery(preflightQ());
  const qc = useQueryClient();
  const fnReseed = useServerFn(reseedBousalaMajors);
  const fnRunQa = useServerFn(runQaFixtures);
  const fnPublish = useServerFn(publishBousala);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin", "bousala", "majors-audit"] });
    qc.invalidateQueries({ queryKey: ["admin", "bousala", "preflight"] });
  };

  const reseed = useMutation({
    mutationFn: () => fnReseed(),
    onSuccess: () => {
      toast.success("تم بذر التخصصات");
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "فشل بذر التخصصات"),
  });

  const runQa = useMutation({
    mutationFn: () => fnRunQa(),
    onSuccess: (res) => {
      toast[res.passed ? "success" : "error"](res.passed ? "كل الفحوصات نجحت" : "فشلت بعض الفحوصات — راجع النتائج");
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "فشل تشغيل الفحوصات"),
  });

  const publish = useMutation({
    mutationFn: () => fnPublish(),
    onSuccess: () => {
      toast.success("تم نشر بوصلة د. سارة");
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "فشل النشر"),
  });

  const pf = preflight.data;
  const majors = audit.data;

  return (
    <div className="space-y-6" dir="rtl">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="kicker text-modern-orange">بوصلة د. سارة</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold">الإعدادات والفحوصات</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            هنا تظهر جاهزية بوصلة للتشغيل: الفحوصات، التخصصات، التسعير، وحالة النشر.
          </p>
        </div>
        <Button type="button" variant="secondary" onClick={refresh} disabled={audit.isFetching || preflight.isFetching}>
          <RefreshCw className={`ms-1 h-4 w-4 ${audit.isFetching || preflight.isFetching ? "animate-spin" : ""}`} />
          تحديث الحالة
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">فحص الجاهزية</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!pf ? (
            <p className="text-sm text-muted-foreground">جاري تحميل حالة الجاهزية…</p>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StateCard label="المراجعة" ok={pf.review.ok} detail={`${pf.review.approved} / ${pf.review.total}`} />
                <StateCard label="فحوصات الجودة" ok={pf.fixtures.ok} detail={pf.fixtures.last_run_at ? new Date(pf.fixtures.last_run_at).toLocaleString("ar-u-nu-latn") : "لم تُشغّل بعد"} />
                <StateCard label="التخصصات" ok={pf.majors.ok} detail={`${pf.majors.seeded} / ${pf.majors.required}`} />
                <StateCard label="التسعير" ok={pf.pricing.ok} detail={`$${pf.pricing.full} / $${pf.pricing.premium}`} />
              </div>
              <Button type="button" onClick={() => runQa.mutate()} disabled={runQa.isPending}>
                {runQa.isPending ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : <PlayCircle className="ms-1 h-4 w-4" />}
                تشغيل فحوصات الجودة
              </Button>
              <Button type="button" onClick={() => publish.mutate()} disabled={!pf.ok || publish.isPending} variant="secondary">
                {publish.isPending ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="ms-1 h-4 w-4" />}
                نشر بوصلة
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            {majors?.ok ? <CheckCircle2 className="h-5 w-5 text-soft-green" /> : <AlertTriangle className="h-5 w-5 text-modern-orange" />}
            قاعدة التخصصات
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!majors ? (
            <p className="text-sm text-muted-foreground">جاري تحميل التخصصات…</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span>المطلوب: <b>{majors.required}</b></span>
                <span>المبذّر: <b>{majors.seeded}</b></span>
                <span>الناقص: <b>{majors.missing.length}</b></span>
                <span>غير المكتمل: <b>{majors.incomplete.length}</b></span>
              </div>
              <Button type="button" onClick={() => reseed.mutate()} disabled={reseed.isPending}>
                {reseed.isPending ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : <RefreshCw className="ms-1 h-4 w-4" />}
                إعادة بذر التخصصات
              </Button>
              <div className="grid gap-4 lg:grid-cols-3">
                <ListCard title="ناقصة" items={majors.missing} />
                <ListCard title="زائدة" items={majors.extra} />
                <ListCard title="غير مكتملة" items={majors.incomplete} />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        أدوات تقنية:{" "}
        <Link
          to="/admin/bousala/narrative-cache"
          className="font-semibold text-foreground underline underline-offset-4"
        >
          ذاكرة الفقرات
        </Link>
      </p>
    </div>

  );
}

function StateCard({ label, ok, detail }: { label: string; ok: boolean; detail: string }) {
  return (
    <div className={`rounded-xl border p-3 ${ok ? "border-soft-green/40 bg-soft-green/10" : "border-modern-orange/40 bg-modern-orange/5"}`}>
      <div className="flex items-center gap-2 text-sm font-bold">
        {ok ? <CheckCircle2 className="h-4 w-4 text-soft-green" /> : <AlertTriangle className="h-4 w-4 text-modern-orange" />}
        {label}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}

function ListCard({ title, items }: { title: string; items: { slug: string; name_ar: string; reason?: string }[] }) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-4">
      <h2 className="font-display text-sm font-bold">{title}</h2>
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">لا شيء</p>
      ) : (
        <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
          {items.map((item) => (
            <li key={item.slug}>• {item.name_ar} <span className="opacity-60">({item.slug})</span>{item.reason ? <span> — {item.reason}</span> : null}</li>
          ))}
        </ul>
      )}
    </div>
  );
}